// Privacy Policy — plain-English version. Discloses what we collect, why,
// and who our third-party processors are. Drafted from scratch to match
// actual data flows in this codebase; not copy-pasted from a template.
//
// Lawyer review recommended before final publication.

import PolicyPage from "./PolicyPage";

export default function PrivacyPolicy() {
  return (
    <PolicyPage title="Privacy Policy" lastUpdated="April 22, 2026">
      <p>
        The Joy of Pancakes is operated by Lucky Bean LLC ("we," "us," "our"). This policy explains what we collect from visitors and customers, why we collect it, and what we do with it. We've written it in plain English.
      </p>

      <div className="jop-policy-callout">
        <strong>Short version:</strong> we collect only what we need to fulfill orders, deliver keepsakes, and send the emails you asked for. We don't sell or trade your data. We use Google Analytics to understand how people use our site — no advertising pixels.
      </div>

      <h2>What we collect</h2>

      <h3>When you place an order</h3>
      <ul>
        <li>Your name, email address, shipping address, and phone (if provided).</li>
        <li>Payment information is collected and processed by <strong>Square</strong>. We do not see or store your full card number — Square handles that.</li>
        <li>Order contents and order history.</li>
      </ul>

      <h3>When you get a pancake at one of our events or livestreams</h3>
      <ul>
        <li>Optional: the name or note you submitted with your order.</li>
        <li>A photo of your pancake, which we process into a personal keepsake page you can view via the ticket's QR code.</li>
        <li>If you enter your email to receive the keepsake, we store that email to send it to you.</li>
      </ul>

      <h3>When you submit a booking inquiry or testimonial</h3>
      <ul>
        <li>The contact info and details you provide in the form.</li>
        <li>Nothing else. We don't run background tracking.</li>
      </ul>

      <h3>Automatic information</h3>
      <ul>
        <li>Basic server-side request logs (IP address, timestamp, page requested) for security, abuse prevention, and rate-limiting. These logs are kept briefly by our hosting provider and not used for advertising.</li>
        <li><strong>Google Analytics (GA4)</strong> collects anonymized usage data (pages visited, session duration, general location, device type) to help us understand how people use the site. Google Analytics does not collect your name, email, or other personal details. You can opt out via <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google's browser add-on</a>.</li>
        <li>We do <strong>not</strong> use Meta Pixel, advertising trackers, or retargeting tools.</li>
      </ul>

      <h2>Why we collect it</h2>
      <ul>
        <li><strong>To fulfill what you asked for:</strong> ship your order, deliver your keepsake, answer your inquiry, respond to your testimonial.</li>
        <li><strong>To communicate with you:</strong> order updates, shipping notifications, keepsake delivery, and — only if you opted in — occasional news about upcoming events or products.</li>
        <li><strong>To protect the service:</strong> rate-limiting bot submissions on public forms.</li>
      </ul>

      <h2>Who we share it with</h2>
      <p>
        We use a small number of trusted vendors to run the business. None of them resell your data, and we only share what each needs to do its job.
      </p>
      <ul>
        <li><strong>Square</strong> — payment processing. Handles your card details per Square's privacy policy.</li>
        <li><strong>Google Firebase</strong> (hosting, authentication, database, storage, cloud functions). Holds our application data, including your order records and keepsake photos. Operated by Google under Google Cloud Platform's privacy terms.</li>
        <li><strong>Brevo</strong> (formerly Sendinblue) — transactional and marketing email (order confirmations, keepsake delivery, opt-in newsletter). Brevo is GDPR-certified.</li>
        <li><strong>Anthropic (Claude)</strong> — we use Claude to help parse customer inquiries and generate accessible alt-text descriptions for pancake photos. Customer PII (names, emails, addresses) is <strong>not</strong> sent to Anthropic — only the photo and pseudonymous context (e.g., a pancake subject name).</li>
        <li><strong>Twitch, YouTube, Instagram, Bluesky</strong> — we link to these but don't embed tracking pixels from them.</li>
      </ul>
      <p>
        We will only disclose your information to government or law enforcement if compelled by valid legal process, and we'll notify you if legally permitted.
      </p>

      <h2>Your rights</h2>
      <p>
        Regardless of where you live, you can:
      </p>
      <ul>
        <li><strong>Ask what we have:</strong> email <a href="mailto:hello@thejoyofpancakes.com">hello@thejoyofpancakes.com</a> and we'll send a copy of what we've stored about you within 30 days.</li>
        <li><strong>Correct it:</strong> ask us to update inaccurate information.</li>
        <li><strong>Delete it:</strong> ask us to remove your account data and keepsake records. Note: we may retain minimal order history (amount, date) as required by tax law.</li>
        <li><strong>Unsubscribe:</strong> every marketing email has an unsubscribe link. Transactional emails (order receipts, shipping) continue as long as you have an active order.</li>
      </ul>

      <h3>California, Virginia, Colorado, and similar state laws</h3>
      <p>
        If you're a resident of a US state with a consumer privacy law (CCPA, VCDPA, CPA, etc.), you have the rights listed above plus a right to opt out of "sale" or "sharing" of personal information. <strong>We don't sell or share personal information for cross-context behavioral advertising</strong> — so there's nothing to opt out of. But you can always email us with any privacy request and we'll respond.
      </p>

      <h3>Children under 13</h3>
      <p>
        We don't knowingly collect personal information from children under 13. If you believe a child has submitted data to us, email <a href="mailto:hello@thejoyofpancakes.com">hello@thejoyofpancakes.com</a> and we'll delete it.
      </p>

      <h2>How long we keep your data</h2>
      <ul>
        <li><strong>Order records:</strong> 7 years (for tax and accounting purposes).</li>
        <li><strong>Keepsake photos + descriptions:</strong> indefinitely, since that's the product — the keepsake URL is meant to work forever. You can request deletion at any time.</li>
        <li><strong>Booking inquiries that don't convert:</strong> deleted or anonymized after 2 years of inactivity.</li>
        <li><strong>Marketing email list:</strong> until you unsubscribe.</li>
      </ul>

      <h2>Security</h2>
      <p>
        We use industry-standard measures: HTTPS everywhere, encrypted storage via Firebase, staff-only access to admin tools, and limited data retention as noted above. No online service is 100% secure, and we'll notify affected customers promptly in the unlikely event of a breach.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        If we materially change how we collect or use data, we'll update this page and note the "Last updated" date at the top. For significant changes, we'll email customers on our list.
      </p>

      <h2>Contact</h2>
      <p>
        Privacy questions or requests:
      </p>
      <p>
        <strong>Lucky Bean LLC</strong><br/>
        P.O. Box 63093<br/>
        St. Louis, MO 63163<br/>
        Email: <a href="mailto:hello@thejoyofpancakes.com">hello@thejoyofpancakes.com</a>
      </p>
    </PolicyPage>
  );
}
